import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { SpecIcons } from '@/components/icons/SpecIcons';
import { useCatalogueExplorerFilterService } from '@/services/catalogue/useCatalogueExplorerFilterService';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { useBuildGroupedSpecifications } from './useBuildGroupedSpecifications';

interface MappedSpec {
  item: string;
  checked: boolean;
}

interface MappedSpecGroup {
  type: string | null;
  specs: MappedSpec[];
}

interface GroupedSpecificationProps {
  group: MappedSpecGroup;
  onCheckCallback: (spec: MappedSpec) => void;
  defaultExpanded?: boolean;
}

const sortSpecs = (items: string[]) => {
  return items.sort((a, b) => {
    const nameA = a.toLowerCase();
    const nameB = b.toLowerCase();

    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  });
};

const buildGroupTitle = (group: MappedSpecGroup) => {
  let name = group.type;
  const selectedFilters = group.specs.filter(s => s.checked);

  if (selectedFilters.length > 0) {
    name += ` (${selectedFilters.length})`;
  }

  return name;
};

const GroupedSpecification: FunctionComponent<GroupedSpecificationProps> = ({
  group,
  onCheckCallback,
  defaultExpanded = false
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className="py-2">
      <div className="flex" onClick={() => setExpanded(!expanded)}>
        <Button onClick={() => setExpanded(!expanded)} variant={ButtonVariant.Ghost}>
          {expanded ? <FiChevronUp /> : <FiChevronDown />}
        </Button>
        <div className="flex space-x-1 items-center">
          <span className="text-2xl">{group.type ? SpecIcons[group.type] : 'N/A'} </span>
          <span className="font-semibold">{buildGroupTitle(group)}</span>
        </div>
      </div>
      <div className={expanded ? 'visible grid grid-cols-3 gap-2 px-3' : 'invisible h-0 overflow-hidden'}>
        {group.specs.map(spec => (
          <div
            key={`${group.type}-${spec.item}`}
            className="flex space-x-2 items-center"
            onClick={() => onCheckCallback(spec)}
          >
            <input checked={spec.checked} className="w-4 h-4" type="checkbox" />
            <p className="text-md">{spec.item}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

interface CatalogueExplorerSpecFilterProps {
  categoryId?: string;
  onChange: (specs: string[]) => void;
}

const CatalogueExplorerSpecFilter: FunctionComponent<CatalogueExplorerSpecFilterProps> = ({ categoryId, onChange }) => {
  const filters = useCatalogueExplorerFilterService(state => state.specFilters);

  const groupedSpecifications = useBuildGroupedSpecifications(categoryId);

  const flatFilters = filters.flatMap(f => f.queries);
  const mapInitialSpecState = () => {
    return groupedSpecifications.map(item => ({
      ...item,
      specs: sortSpecs(item.specs).map(spec => ({ item: spec, checked: flatFilters.includes(spec) }))
    })) as MappedSpecGroup[];
  };

  const [specifications, setSpecifications] = useState(mapInitialSpecState());

  useEffect(() => {
    setSpecifications(mapInitialSpecState());
  }, [categoryId]);

  const onChecked = (spec: MappedSpec) => {
    spec.checked = !spec.checked;
    setSpecifications([...specifications]);

    const checkedItems = specifications.flatMap(item => item.specs.filter(spec => spec.checked).map(spec => spec.item));

    onChange(checkedItems);
  };

  const filteredSpecCount = useMemo(() => {
    return specifications.reduce((count, category) => {
      return count + category.specs.filter(spec => spec.checked).length;
    }, 0);
  }, [specifications]);

  return (
    <div className="space-y-1">
      <p className="text-lg font-medium">Specifications {filteredSpecCount > 0 && `(${filteredSpecCount})`}</p>
      <div className="flex flex-col gap-1 max-h-[20rem] overflow-auto [&>*]:border-b">
        {specifications.map(group => (
          <GroupedSpecification
            key={group.type}
            group={group}
            onCheckCallback={onChecked}
            defaultExpanded={group.specs.filter(s => s.checked).length > 0}
          />
        ))}
      </div>
    </div>
  );
};

export default CatalogueExplorerSpecFilter;
